import {
  call,
  put,
  StrictEffect,
  takeEvery,
  select,
  takeLatest,
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import asyncErrorHandler from 'src/utils/asyncErrorHandler';
import { PushTokensResponse } from 'src/types';
import {
  login,
  setAuthData,
  setAuthLoading,
  setLoginLoading,
  loadUser,
  updateAuthData,
  registerPushToken,
  updatePushToken,
  changeCustomer,
} from './reducer';
import { getProjectRetainer } from '../projectRetainer/reducer';
import { LOCAL_STORAGE_PROJECT_UUID_KEY } from 'src/utils/constants';

/**
 * worker saga
 */
const { post, get, put: putApi } = apiRequests;

export function* loginSagaListener(action: any): any {
  try {
    yield put(setLoginLoading(true));

    const res = yield call(post, apiRoutes.LOGIN, {
      cookie_login: false,
      ...action.payload,
    });

    yield put(setAuthData(res.data));
    yield put(getProjectRetainer());
  } catch (error) {
    yield put(setLoginLoading(false));

    asyncErrorHandler(error);
  }
}

export function* loadUserSagaListener({
  payload,
}: {
  payload?: { onLoaded?: () => void };
  type: string;
}): any {
  const token = yield select((state) => state.auth.token);

  if (!token) {
    yield put(setAuthLoading(false));
    return;
  }

  try {
    yield put(setAuthLoading(true));

    const res: AxiosResponse = yield call(
      get,
      apiRoutes.USER_INFO,
      {},
      {},
      true
    );

    yield put(getProjectRetainer());
    yield put(updateAuthData(res.data));
    payload?.onLoaded?.();
  } catch (error) {
    yield put(setAuthLoading(false));
  }
}

export function* changeCustomerSagaListener({
  payload,
}: {
  payload: string;
  type: string;
}): any {
  const user = yield select((state) => state.auth.user);

  yield put({ type: 'store/reset' });
  yield put(updateAuthData(null));
  yield put(setAuthLoading(true));

  try {
    const currentProject = user.projects.find(
      (project: any) => project.customer.uuid === payload
    );

    if (currentProject) {
      localStorage.setItem(LOCAL_STORAGE_PROJECT_UUID_KEY, currentProject.uuid);
    }
  } catch (error) {
    asyncErrorHandler(error);
  } finally {
    yield put(loadUser());
    yield put(getProjectRetainer());
    yield put(setAuthLoading(false));
  }
}

export function* registerPushTokenListener({
  payload: newToken,
}: {
  payload: string;
  type: string;
}): any {
  const { push_tokens }: { push_tokens: PushTokensResponse[] | null } =
    yield select((state) => state.auth.user);
  const tokens = push_tokens ?? [];

  const prevTokenIndex = tokens.findIndex(
    (value) => value.token.split(':')[0] === newToken.split(':')[0]
  );

  try {
    if (prevTokenIndex > -1) {
      const prevToken = tokens[prevTokenIndex];

      yield call(putApi, `${apiRoutes.FCM_TOKEN}/${prevToken.token}`, {
        token: newToken,
      });

      const newTokens = [...tokens];

      newTokens[prevTokenIndex] = {
        ...newTokens[prevTokenIndex],
        token: newToken,
      };

      yield put(updatePushToken(newTokens));
    } else {
      const res: AxiosResponse = yield call(post, apiRoutes.FCM_TOKEN, {
        token: newToken,
      });

      yield put(updatePushToken([...tokens, res.data.data]));
    }
  } catch (error) {
    asyncErrorHandler(error);
  }
}

/**
 * watcher saga
 */
function* authSaga(): Generator<StrictEffect> {
  yield takeEvery(login.type, loginSagaListener);
  yield takeLatest(loadUser.type, loadUserSagaListener);
  yield takeLatest(changeCustomer.type, changeCustomerSagaListener);
  yield takeEvery(registerPushToken.type, registerPushTokenListener);
}

export default authSaga;
